import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { apiCall } from '@/utils/apiAxios';
import { getTennantCompany } from '@/config/configHelpers';
import { AccountInfo } from '@azure/msal-browser';
import { useAgentStore } from '@/stores/agent';
import { useDashboardStore } from '@/stores/dashboard/main/dashboard_v1';
import { Profile } from '@/types/user/user';
import { showApiError } from '@/utils/vueSweetalert2';
import * as Sentry from '@sentry/vue';
import { safeStorage } from '@/utils/safeStorage';

export const useAuthStore = defineStore('auth', () => {
    const agentStore = useAgentStore();
    const dashboardStore = useDashboardStore();
    const router = useRouter();
    const route = useRoute();

    // State
    const user = ref<Profile | null>(safeStorage.getJSON<Profile | null>('user', null));
    const sessionHash = ref(safeStorage.getItem('session_hash') || '');
    const xApiKey = ref(safeStorage.getItem('apikey') || null);
    const returnUrl = ref<string | null>(null);
    const returnName = ref<string | null>(null);
    const validEmail = ref(false);
    const loginValidateEmail = ref(false);
    const acceptanceValidation = ref(false);
    const apiAcceptance = ref(false);
    const isSubmitting = ref(false);
    const groups = ref(safeStorage.getJSON<unknown[]>('groups', []));

    const imgEmptyUserImage = ref('');

    // Varibles de impersonación
    const impersonate = ref(false);
    const impersonateUser = ref(null);
    // 1 = sin permisos, 2 = con permisos de ver todo
    const impersonateLevel = ref<number>(safeStorage.getJSON<number>('impersonateLevel', 2, 'session'));
    const impersonateLoading = ref(true);
    const impersonateError = ref(false);

    const isLoadingLogin = ref(false);

    // Actions
    const login = async (username: string, password: string, acceptance: boolean, hash: string | string[]) => {
        isLoadingLogin.value = true;

        const data_post: Record<string, unknown> = {
            username,
            password,
            module: 'brokerin',
            acceptance
        };

        const tenant = getTennantCompany();
        if (typeof tenant === 'string' && tenant.trim() !== '') {
            data_post.tennant_company = tenant.trim();
        }

        const { request } = await apiCall({
            method: 'post',
            url: `/auth/api/login`,
            data: data_post
        });

        try {
            const response = await request;

            if (response.status == 1) {
                user.value = response.info;
                xApiKey.value = response.api_key;
                groups.value = response.group;
                sessionHash.value = response.info.user_session_time_hash;

                impersonateLevel.value = 2;

                const firstnameInitial = user.value?.first_name?.charAt(0) || 'N';
                const lastnameInitial = user.value?.last_name?.charAt(0) || 'A';

                imgEmptyUserImage.value = `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`;

                safeStorage.setItem('user', JSON.stringify(response.info));
                safeStorage.setItem('session_hash', response.info.user_session_time_hash);
                safeStorage.setItem('groups', JSON.stringify(response.group));
                safeStorage.setItem('apikey', response.api_key);
                safeStorage.setItem('date-login', new Date().toISOString());
                safeStorage.setItem('impersonateLevel', JSON.stringify(impersonateLevel.value), 'session');

                /*
                 * El throttle de 50 min de `getAdvertisements()` (advertisement.ts) guarda su
                 * marca en `sessionStorage`, que sobrevive a un "logout silencioso" (el
                 * interceptor 403 de apiAxios.ts solo limpia `localStorage`). Sin este reset,
                 * un login nuevo en la misma pestaña puede no volver a pedir los anuncios
                 * pendientes de acuse si no pasaron los 50 min desde la sesión anterior.
                 */
                try {
                    sessionStorage.removeItem('timeGetAdvertisements');
                } catch {
                    /* almacenamiento no disponible */
                }

                // Configurar usuario en Sentry
                if (import.meta.env.PROD) {
                    Sentry.setUser({
                        id: response.info.id?.toString(),
                        email: response.info.email,
                        username: `${response.info.first_name} ${response.info.last_name}`
                    });
                }

                try {
                    // `Secure` solo sobre HTTPS: Firefox descarta una cookie Secure en
                    // http://localhost (Chromium hace excepción para localhost, Firefox no).
                    const secureFlag = location.protocol === 'https:' ? ' Secure;' : '';
                    document.cookie = `X-API-KEY=${response.api_key}; SameSite=Lax;${secureFlag} path=/; max-age=86400`; // Expira en 1 día
                } catch {
                    /* cookies no disponibles */
                }
                // SameSite=None es para que el cookie no afecte a la navegación por que el dominio son diferentes
                if (hash == '' || hash === undefined) {
                    if (returnName.value == '' || returnName.value === undefined || returnName.value === null) {
                        router.push({
                            name: 'Mi Tablero v2'
                        });
                    } else {
                        router.push({
                            name: returnName.value
                        });
                    }
                } else {
                    // v1 cerrada: destino directo en Mis Folios v2 Detalle (antes
                    // `{ name: 'Detalle de la Actividad', params: { hash_folio } }`,
                    // ruta v1 ya bloqueada; esa vista v2 usa `:hash`, no `:hash_folio`).
                    router.push({
                        name: 'Mis Folios v2 Detalle',
                        params: {
                            hash: route.params.hash_folio
                        }
                    });
                }

                
                setTimeout(() => {
                    validEmail.value = false;
                    apiAcceptance.value = false;
                    acceptanceValidation.value = false;
                    isLoadingLogin.value = false;
                }, 2000);
            } else {
                setTimeout(() => {
                    isLoadingLogin.value = false;
                }, 1000);

                return response.message;
            }
        } catch (error) {
            const errorMessage = 'Error del servidor. Por favor, intenta nuevamente.';
            setTimeout(() => {
                isLoadingLogin.value = false;
            }, 1000);

            return errorMessage;
        }
    };

    const impersonateLogin = async () => {
        impersonateLoading.value = true;

        const data_post = {
            login_hash: route.params.hash
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/users/impersonate_login`,
            data: data_post
        });

        const response = await request;

        impersonateLoading.value = false;

        if (response.status == 1) {
            user.value = response.response.info;
            xApiKey.value = response.response.api_key;
            groups.value = response.response.groups;
            sessionHash.value = response.response.info.user_session_time_hash;

            impersonateLevel.value = route.params.restricted == 'XLD' ? 2 : 1;

            const firstnameInitial = user.value?.first_name?.charAt(0) || 'N';
            const lastnameInitial = user.value?.last_name?.charAt(0) || 'A';

            imgEmptyUserImage.value = `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`;

            safeStorage.setItem('user', JSON.stringify(response.response.info));
            safeStorage.setItem('session_hash', response.response.info.user_session_time_hash);
            safeStorage.setItem('groups', JSON.stringify(response.response.groups));
            safeStorage.setItem('apikey', response.response.api_key);

            // Configurar usuario en Sentry (impersonate)
            if (import.meta.env.PROD) {
                Sentry.setUser({
                    id: response.response.info.id?.toString(),
                    email: response.response.info.email,
                    username: `${response.response.info.first_name} ${response.response.info.last_name} (Impersonate)`
                });
            }
            safeStorage.setItem('date-login', new Date().toISOString());
            safeStorage.setItem('impersonateLevel', JSON.stringify(impersonateLevel.value), 'session');

            try {
                const secureFlag = location.protocol === 'https:' ? ' Secure;' : '';
                document.cookie = `X-API-KEY=${response.response.api_key}; SameSite=Lax;${secureFlag} path=/; max-age=86400`; // Expira en 1 día
            } catch {
                /* cookies no disponibles */
            }
            // SameSite=None es para que el cookie no afecte a la navegación por que el dominio son diferentes

            router.push({
                name: 'Mi Tablero v2'
            });

            validEmail.value = false;
            apiAcceptance.value = false;
            acceptanceValidation.value = false;
        } else {
            impersonateError.value = true;
            return response.message;
        }
    };

    const loginMicrosoft = async (username: string, token: string) => {
        const data_post = {
            username,
            token,
            provider: 4
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/auth/api/login/login_social`,
            data: data_post
        });

        const response = await request;

        if (response.status == 1) {
            user.value = response.info;
            xApiKey.value = response.api_key;
            groups.value = response.group;

            safeStorage.setItem('user', JSON.stringify(response.info));
            safeStorage.setItem('groups', JSON.stringify(response.group));
            safeStorage.setItem('apikey', response.api_key);

            // Mismo reset de throttle de anuncios que en `login()` — ver comentario ahí.
            try {
                sessionStorage.removeItem('timeGetAdvertisements');
            } catch {
                /* almacenamiento no disponible */
            }

            // Configurar usuario en Sentry
            if (import.meta.env.PROD) {
                Sentry.setUser({
                    id: response.info.id?.toString(),
                    email: response.info.email,
                    username: `${response.info.first_name} ${response.info.last_name}`
                });
            }
            safeStorage.setItem('date-login', new Date().toISOString());

            router.push({
                name: 'Mi Tablero v2'
            });

            validEmail.value = false;
            apiAcceptance.value = false;
            acceptanceValidation.value = false;
        } else {
            console.error(response.message);
        }
    };

    const validateEmail = async (username: string) => {
        loginValidateEmail.value = true;
        isSubmitting.value = true;

        console.log(username);
        

        try {
            const data = {
                email: username,
                module: 'brokerin'
            };

            const { request } = await apiCall({
                method: 'get',
                url: `/auth/api/login/validate_mail`,
                data: {},
                params: data
            });

            const response = await request;

            if (response.status == 1) {
                validEmail.value = true;

                apiAcceptance.value = response.response.acceptance;
                acceptanceValidation.value = response.response.acceptance;

                loginValidateEmail.value = false;
                isSubmitting.value = false;
                return true;
            } else {
                loginValidateEmail.value = false;
                isSubmitting.value = false;
                return response.response;
            }
        } catch (error) {
            console.error(error + ' (validateEmail)');
            return error;
        }
    };

    const logout = async (redirect = true) => {
        if (redirect) {
            const agentHash = safeStorage.getItem('agentHash', 'session');
            const actionId = 14;
            try {
                const { request } = apiCall({
                    method: 'post',
                    url: '/brokerin/api/users_session/register_user_action',
                    data: {
                        session_hash: sessionHash.value,
                        action_id: actionId,
                        agent_hash: agentHash
                    }
                });
                await request;
            } catch (error) {
                console.error('Error al registrar la acción de logout:', error);
            }
        }

        isSubmitting.value = false;
        user.value = null;
        xApiKey.value = null;
        impersonate.value = false;
        impersonateLevel.value = 0;
        
        // Limpiar usuario de Sentry
        if (import.meta.env.PROD) {
            Sentry.setUser(null);
        }
        
        try {
            localStorage.removeItem('user');
            localStorage.removeItem('apikey');
            localStorage.removeItem('timeToLogout');
            localStorage.clear();
            sessionStorage.clear();
        } catch {
            /* almacenamiento no disponible */
        }
        try {
            document.cookie = 'X-API-KEY=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        } catch {
            /* cookies no disponibles */
        }
        agentStore.resetAllVariables();
        dashboardStore.resetAllVariables();
        if (redirect) {
            window.location.href = '/';
        }
        return;
    };

    const account = ref<AccountInfo | null>(null);
    const token = ref<string | null>(null);

    const setAccount = (accountInfo: AccountInfo) => {
        account.value = accountInfo;
    };

    const setToken = (accessToken: string) => {
        token.value = accessToken;
    };

    function getUserGroupByName(groupName: string) {
        return groups.value.find((group: { name: string }) => group.name == groupName);
    }

    const validatePassword = async (password: string, password_confirm: string, token: string) => {
        try {
            const url = `/brokerin/api/auth/br_password_recovery`;
            const data = {
                password: password,
                password_confirm: password_confirm,
                token: token
            };

            const { request } = await apiCall({
                method: 'post',
                url: url,
                data
            });

            const response = await request;

            return response.status;

            // if (response.status == 1) {
            //     alertStore.addAlert({
            //         message: `La nueva contraseña se guardó con éxito.`,
            //         type: 'success',
            //         duration: 4000
            //     });
            //     return response.status;
            // } else {
            //     showApiError(url, response, null);
            // }
        } catch (error) {
            console.error(error + ' (validateEmail)');
            return error;
        }
    };

    const validateToken = async (token: string | string[]) => {
        try {
            const url = `/brokerin/api/auth/br_verify_token`;
            const data = {
                token: token
            };

            const { request } = await apiCall({
                method: 'post',
                url: url,
                data
            });

            const response = await request;
            if (response.status == 0) {
                showApiError(url, response, null);
            }

            return response.response;
        } catch (error) {
            console.error(error + ' (br_verify_token)');
            return error;
        }
    };

    return {
        user,
        groups,
        xApiKey,
        sessionHash,
        returnUrl,
        returnName,
        validEmail,
        loginValidateEmail,
        acceptanceValidation,
        apiAcceptance,
        account,
        token,
        isSubmitting,
        isLoadingLogin,
        impersonate,
        impersonateUser,
        impersonateLevel,
        impersonateLoading,
        impersonateError,
        imgEmptyUserImage,
        setAccount,
        setToken,
        login,
        impersonateLogin,
        loginMicrosoft,
        validateEmail,
        getUserGroupByName,
        logout,
        validatePassword,
        validateToken
    };
});
